/**
 * NoteSplitPanel.jsx — Muhasebe FAZ 4.8, "Senet Parçala" paneli.
 *
 * Uç: POST /admin/promissory-notes/{note_id}/split
 * Body: { parts: [{ due_date, amount? }, ...] }  (2–60 parça)
 *
 * Tutar önizlemesi backend ile BİREBİR aynı formülden gelir
 * (promissoryMath.js::splitPreviewAmounts — per = AŞAĞI yuvarlanmış
 * (base/n); son parça = base - per*(n-1), kalanın TAMAMI son parçaya gider
 * ve matematiksel olarak diğerlerinden KÜÇÜK OLAMAZ — B8). Burada kendi
 * yuvarlama stratejisi YAZILMAZ; kuruş artığının SON parçaya gittiği
 * ekranda açıkça belirtilir.
 *
 * `amount` ya TÜM parçalarda gönderilir ya HİÇBİRİNDE (backend sözleşmesi):
 * "tutarları elle gir" kapalıyken payload'a hiç `amount` konmaz, backend
 * eşit böler — önizleme ile birebir aynı sonucu üretir.
 */
import { useEffect, useMemo, useState } from 'react';
import { Scissors } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import { useConfirmDialog } from '../../../components/admin';
import { fmtMoneyAccounting } from '../../../utils/money';
import { useSplitPromissoryNote } from '../../../api/queries/accounting';
import {
  ActionButton,
  Field,
  INPUT_CLASS,
  InlineAlert,
  NotePicker,
  TabIntro,
  isReconciliationError,
  noteErrorMessage,
} from './noteUiKit';
import {
  SPLIT_MAX_PARTS,
  SPLIT_MIN_PARTS,
  amountsMatchTotal,
  childNoteNumberPreview,
  isActiveNote,
  isValidDueDate,
  splitPreviewAmounts,
  sumAmounts,
} from './promissoryMath';

/** Dizi boyutunu koruyarak yeniden boyutlandırır (mevcut girdiler kaybolmaz). */
function resize(list, size, filler = '') {
  const next = list.slice(0, size);
  while (next.length < size) next.push(filler);
  return next;
}

export default function NoteSplitPanel({ userId, notes }) {
  const { t, lang } = useLanguage();
  const toast = useToast();
  const confirm = useConfirmDialog();
  const splitNote = useSplitPromissoryNote(userId);

  const [noteId, setNoteId] = useState('');
  const [partCount, setPartCount] = useState(SPLIT_MIN_PARTS);
  const [manual, setManual] = useState(false);
  const [dueDates, setDueDates] = useState(() => resize([], SPLIT_MIN_PARTS));
  const [amounts, setAmounts] = useState(() => resize([], SPLIT_MIN_PARTS));
  const [submitted, setSubmitted] = useState(false);
  const [stuck, setStuck] = useState(false);

  const activeNotes = useMemo(() => (notes || []).filter((n) => isActiveNote(n.status)), [notes]);
  const note = activeNotes.find((n) => n.note_id === noteId) || null;

  const countValid = Number.isInteger(partCount) && partCount >= SPLIT_MIN_PARTS && partCount <= SPLIT_MAX_PARTS;

  useEffect(() => {
    if (!countValid) return;
    setDueDates((prev) => resize(prev, partCount));
    setAmounts((prev) => resize(prev, partCount));
  }, [partCount, countValid]);

  // Eşit bölme önizlemesi — backend ile birebir aynı formül.
  const previewAmounts = useMemo(
    () => (note && countValid ? splitPreviewAmounts(note.amount, partCount) : []),
    [note, partCount, countValid]
  );

  const shownAmounts = manual ? amounts.map((v) => (v === '' ? NaN : Number(v))) : previewAmounts;
  const shownTotal = manual ? sumAmounts(amounts.map((v) => Number(v) || 0)) : sumAmounts(previewAmounts);
  // "Eşit bölünür" bilgi kutusu SADECE checkbox state'inden türer — ara
  // state/effect YOK, bu yüzden manuel moda geçişte bayat kalmaz.
  const showEqualSplitHint = !manual;

  const missingDue = dueDates.slice(0, partCount).some((d) => !isValidDueDate(d));
  const missingAmount = manual && amounts.slice(0, partCount).some((v) => v === '' || Number.isNaN(Number(v)));
  const totalMismatch =
    manual && !missingAmount && note ? !amountsMatchTotal(amounts.map(Number), note.amount) : false;

  const canSubmit = !!note && countValid && !missingDue && !missingAmount && !totalMismatch;

  const reset = () => {
    setNoteId('');
    setPartCount(SPLIT_MIN_PARTS);
    setManual(false);
    setDueDates(resize([], SPLIT_MIN_PARTS));
    setAmounts(resize([], SPLIT_MIN_PARTS));
    setSubmitted(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!canSubmit) return;

    const ok = await confirm({
      title: t('accounting.notes.split.confirm_title'),
      description: `${t('accounting.notes.split.confirm_description')} ${t('accounting.notes.irreversible')}`,
      variant: 'danger',
      confirmLabel: t('accounting.notes.confirm_continue'),
    });
    if (!ok) return;

    const parts = dueDates.slice(0, partCount).map((due, i) =>
      manual ? { due_date: due, amount: Number(amounts[i]) } : { due_date: due }
    );

    try {
      setStuck(false);
      await splitNote.mutateAsync({ noteId: note.note_id, parts });
      toast.success(t('accounting.notes.split.success'));
      reset();
    } catch (err) {
      if (isReconciliationError(err)) setStuck(true);
      toast.error(noteErrorMessage(err, lang, t));
    }
  };

  return (
    <form className="space-y-4" onSubmit={onSubmit} noValidate>
      <TabIntro
        title={t('accounting.notes.split.title')}
        description={t('accounting.notes.split.description')}
      />

      {stuck && (
        <InlineAlert variant="danger" title={t('accounting.notes.reconcile_title')} testId="note-split-reconcile-alert">
          {t('accounting.notes.reconcile_desc')}
        </InlineAlert>
      )}

      <NotePicker
        id="note-split-note"
        testId="note-split-select"
        notes={activeNotes}
        value={noteId}
        onChange={(v) => { setNoteId(v); setSubmitted(false); }}
        label={t('accounting.notes.split.note_label')}
        required
        placeholder={t('accounting.notes.select_placeholder')}
        emptyText={t('accounting.notes.split.no_active_notes')}
        error={submitted && !noteId ? t('accounting.notes.note_required') : ''}
        lang={lang}
        t={t}
      />

      {note && (
        <>
          <Field
            id="note-split-count"
            label={t('accounting.notes.split.count_label')}
            required
            hint={t('accounting.notes.split.count_hint')}
            error={submitted && !countValid ? t('accounting.notes.split.count_error') : ''}
          >
            {({ id, describedBy }) => (
              <input
                id={id}
                type="number"
                min={SPLIT_MIN_PARTS}
                max={SPLIT_MAX_PARTS}
                step="1"
                className={INPUT_CLASS}
                value={partCount}
                aria-required
                aria-invalid={submitted && !countValid ? true : undefined}
                aria-describedby={describedBy}
                onChange={(e) => setPartCount(parseInt(e.target.value, 10) || 0)}
                data-testid="note-split-count-input"
              />
            )}
          </Field>

          <label className="flex items-center gap-2 text-xs font-bold cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-2 border-[var(--border-light)] accent-[var(--brand-primary)]"
              checked={manual}
              onChange={(e) => setManual(e.target.checked)}
              data-testid="note-split-manual-toggle"
            />
            {t('accounting.notes.split.manual_label')}
          </label>

          {showEqualSplitHint && (
            <InlineAlert variant="info" testId="note-split-rounding-note">
              {t('accounting.notes.split.equal_hint')}
            </InlineAlert>
          )}

          <div className="rounded-xl border-2 border-[var(--border-light)] overflow-hidden">
            <div className="px-3 py-2 bg-[var(--bg-app)] text-[11px] font-bold uppercase tracking-wider text-[var(--text-muted)]">
              {t('accounting.notes.split.preview_title')}
            </div>
            <ul className="divide-y divide-[var(--border-light)]">
              {Array.from({ length: countValid ? partCount : 0 }).map((_, i) => (
                <li
                  key={i}
                  className="flex flex-wrap items-end gap-2 p-2.5"
                  data-testid={`note-split-preview-row-${i}`}
                >
                  <span className="text-xs font-bold w-20 flex-shrink-0">
                    {`${t('accounting.notes.split.part')} ${i + 1}`}
                    {childNoteNumberPreview(note.note_number, i + 1) && (
                      <span className="block text-[10px] font-normal text-[var(--text-muted)]">
                        {childNoteNumberPreview(note.note_number, i + 1)}
                      </span>
                    )}
                  </span>
                  <div className="flex-1 min-w-[150px]">
                    <label htmlFor={`note-split-due-${i}`} className="block text-[10px] font-bold text-[var(--text-muted)]">
                      {t('accounting.notes.split.due_label')}
                    </label>
                    <input
                      id={`note-split-due-${i}`}
                      type="date"
                      className={INPUT_CLASS}
                      value={dueDates[i] || ''}
                      aria-required
                      onChange={(e) => setDueDates((prev) => prev.map((d, idx) => (idx === i ? e.target.value : d)))}
                      data-testid={`note-split-due-input-${i}`}
                    />
                  </div>
                  <div className="flex-1 min-w-[130px]">
                    <label htmlFor={`note-split-amount-${i}`} className="block text-[10px] font-bold text-[var(--text-muted)]">
                      {t('accounting.notes.split.amount_label')}
                    </label>
                    {manual ? (
                      <input
                        id={`note-split-amount-${i}`}
                        type="number"
                        step="0.01"
                        min="0"
                        className={INPUT_CLASS}
                        value={amounts[i] ?? ''}
                        onChange={(e) => setAmounts((prev) => prev.map((a, idx) => (idx === i ? e.target.value : a)))}
                        data-testid={`note-split-amount-input-${i}`}
                      />
                    ) : (
                      <p
                        id={`note-split-amount-${i}`}
                        className="px-3 py-2 text-sm font-bold tabular-nums"
                        data-testid={`note-split-preview-amount-${i}`}
                      >
                        {fmtMoneyAccounting(shownAmounts[i], lang)}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between px-3 py-2 bg-[var(--bg-app)] text-xs font-bold">
              <span>{t('accounting.notes.split.total_label')}</span>
              <span className="tabular-nums" data-testid="note-split-total">
                {fmtMoneyAccounting(shownTotal, lang)} / {fmtMoneyAccounting(note.amount, lang)}
              </span>
            </div>
          </div>

          {submitted && missingDue && (
            <InlineAlert variant="danger" testId="note-split-due-error">
              {t('accounting.notes.split.due_required')}
            </InlineAlert>
          )}
          {submitted && missingAmount && (
            <InlineAlert variant="danger" testId="note-split-amount-error">
              {t('accounting.notes.split.amount_required')}
            </InlineAlert>
          )}
          {totalMismatch && (
            <InlineAlert variant="danger" testId="note-split-total-error">
              {t('accounting.notes.split.total_mismatch')}
            </InlineAlert>
          )}

          <div className="flex justify-end">
            <ActionButton
              type="submit"
              variant="danger"
              icon={Scissors}
              loading={splitNote.isPending}
              disabled={splitNote.isPending}
              testId="note-split-submit"
            >
              {splitNote.isPending ? t('accounting.notes.submitting') : t('accounting.notes.split.submit')}
            </ActionButton>
          </div>
        </>
      )}
    </form>
  );
}
